import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiArrowLeft, mdiAutoFix, mdiPlus, mdiSend, mdiShimmer } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import { useQueryClient } from '@tanstack/react-query'
import {
  useCapexPlan,
  useUpdateWorksite,
  useDeleteWorksite,
  useDeleteQuote,
  useSubmitCapexPlan,
  useApproveCapexPlan,
  useRejectCapexPlan,
  useGenerateWorksites,
} from '../../hooks/useCapexQueries'
import { useAsset } from '../../hooks/useQueries'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { smoothPlan } from '../../lib/capex-utils'
import type { FilterTab, WorkflowStatus, Worksite, WorkPackage } from './types'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PlanGrid } from './PlanGrid'
import { KPIStrip } from './KPIStrip'
import { useCan } from '../../contexts/PermissionsContext'
import { WorkflowBanners } from './WorkflowBanners'
import { SubmitModal } from './SubmitModal'
import { RejectModal } from './RejectModal'
import { CreateWorksiteDialog } from './CreateWorksiteDialog'
import { EditWorksiteDialog } from './EditWorksiteDialog'
import { QuoteDialog } from './QuoteDialog'
import { CreateWorkPackageDialog } from './CreateWorkPackageDialog'
import { EditWorkPackageDialog } from './EditWorkPackageDialog'
import { CapexCurrencyProvider } from './CapexCurrencyContext'

interface CapexPlanDetailProps {
  org: string
  planUid: string
}

const WORKFLOW_BADGE_VARIANTS: Record<WorkflowStatus, 'secondary' | 'default' | 'destructive'> = {
  draft: 'secondary',
  submitted: 'default',
  approved: 'default',
  rejected: 'destructive',
}

// ── URL state helpers ─────────────────────────────────────────────────────

function getFilterFromUrl(): FilterTab {
  const sp = new URLSearchParams(window.location.search)
  const f = sp.get('filter')
  if (f === 'compliance' || f === 'pending' || f === 'validated' || f === 'deferred') {
    return f
  }
  return 'all'
}

function getCollapsedFromUrl(): Set<string> {
  const sp = new URLSearchParams(window.location.search)
  const c = sp.get('collapsed')
  if (!c) return new Set()
  return new Set(c.split(',').filter(Boolean))
}

function getWorksiteFromUrl(): string | null {
  const sp = new URLSearchParams(window.location.search)
  return sp.get('worksite')
}

function updateUrl(filter: FilterTab, collapsed: Set<string>, worksiteUid?: string | null) {
  const url = new URL(window.location.href)
  if (filter === 'all') {
    url.searchParams.delete('filter')
  } else {
    url.searchParams.set('filter', filter)
  }
  const collapsedArr = Array.from(collapsed)
  if (collapsedArr.length === 0) {
    url.searchParams.delete('collapsed')
  } else {
    url.searchParams.set('collapsed', collapsedArr.join(','))
  }
  // Only update worksite param if explicitly provided (not undefined)
  if (worksiteUid !== undefined) {
    if (worksiteUid) {
      url.searchParams.set('worksite', worksiteUid)
    } else {
      url.searchParams.delete('worksite')
    }
  }
  window.history.replaceState({}, '', url.toString())
}

// ── PlanToolbar ─────────────────────────────────────────────────────────

function PlanToolbar({
  filter,
  onFilterChange,
  worksites,
  isDraft,
  onAddWorksite,
  onSmooth,
  onGenerate,
  isGenerating,
  breachCount,
  overdueCount,
  unlockedRent,
  sortByUnlockedRent,
  onToggleSort,
}: {
  filter: FilterTab
  onFilterChange: (f: FilterTab) => void
  worksites: Worksite[]
  isDraft: boolean
  onAddWorksite: () => void
  onSmooth: () => void
  onGenerate: () => void
  isGenerating: boolean
  breachCount?: number
  overdueCount?: number
  unlockedRent?: import('./types').UnlockedRent
  sortByUnlockedRent: boolean
  onToggleSort: () => void
}) {
  const { t } = useTranslation('capex')

  const filterTabs: FilterTab[] = ['all', 'compliance', 'pending', 'validated', 'deferred']

  return (
    <div className="space-y-3">
      {/* KPI strip */}
      <KPIStrip worksites={worksites} breachCount={breachCount} overdueCount={overdueCount} unlockedRent={unlockedRent} />

      {/* Filter tabs + actions */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          {filterTabs.map((tab) => (
            <Button
              key={tab}
              variant={filter === tab ? 'default' : 'outline'}
              size="sm"
              onClick={() => onFilterChange(tab)}
              aria-label={`Filter: ${t(`filter.${tab}`, tab)}`}
              aria-pressed={filter === tab}
              data-testid={`filter-tab-${tab}`}
            >
              {t(`filter.${tab}`, tab)}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {unlockedRent && unlockedRent.lease_count > 0 && (
            <Button
              size="sm"
              variant={sortByUnlockedRent ? 'default' : 'outline'}
              onClick={onToggleSort}
              aria-pressed={sortByUnlockedRent}
              data-testid="capex-sort-unlocked-rent"
            >
              {t('kpi.sort_unlocked_rent')}
            </Button>
          )}
          {isDraft && (
            <Button
              size="sm"
              variant="outline"
              onClick={onGenerate}
              disabled={isGenerating}
              data-testid="generate-worksites-btn"
            >
              <AppIcon path={mdiAutoFix} size="sm" className="mr-1.5" />
              {isGenerating ? t('generate.pending') : t('generate.button')}
            </Button>
          )}
          {isDraft && worksites.length > 1 && (
            <Button
              size="sm"
              variant="outline"
              onClick={onSmooth}
              data-testid="smooth-btn"
            >
              <AppIcon path={mdiShimmer} size="sm" className="mr-1.5" />
              {t('smooth.button')}
            </Button>
          )}
          {isDraft && (
            <Button
              size="sm"
              variant="outline"
              onClick={onAddWorksite}
              data-testid="add-worksite-btn"
            >
              <AppIcon path={mdiPlus} size="sm" className="mr-1.5" />
              {t('detail.add_worksite')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────────

export function CapexPlanDetail({ org, planUid }: CapexPlanDetailProps) {
  const { t } = useTranslation('capex')

  // Modal state
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showCreateWorksite, setShowCreateWorksite] = useState(false)
  const [showSmoothDialog, setShowSmoothDialog] = useState(false)
  const [smoothPreview, setSmoothPreview] = useState<{ movedCount: number; protectedCount: number; movedWorksites: Worksite[] } | null>(null)
  const [showQuoteDialog, setShowQuoteDialog] = useState(false)
  const [quotePackage, setQuotePackage] = useState<WorkPackage | null>(null)
  const [showDeleteQuoteDialog, setShowDeleteQuoteDialog] = useState(false)
  const [deleteQuotePackage, setDeleteQuotePackage] = useState<WorkPackage | null>(null)
  const [showCreatePackageDialog, setShowCreatePackageDialog] = useState(false)
  const [createPackageWorksiteUid, setCreatePackageWorksiteUid] = useState<string | null>(null)
  const [showEditWorksiteDialog, setShowEditWorksiteDialog] = useState(false)
  const [editWorksite, setEditWorksite] = useState<Worksite | null>(null)
  const [showEditPackageDialog, setShowEditPackageDialog] = useState(false)
  const [editPackage, setEditPackage] = useState<WorkPackage | null>(null)

  const { data: plan, isLoading, error } = useCapexPlan(org, planUid, {
    include: 'worksites.work_packages',
  })

  const { data: asset } = useAsset(org, plan?.asset_uid ?? '')

  // URL-driven filter state
  const [filter, setFilter] = useState<FilterTab>(getFilterFromUrl)
  const [sortByUnlockedRent, setSortByUnlockedRent] = useState(false)
  // The rent is a contract figure: the server omits it without contracts.read, and the UI follows.
  const canReadContracts = useCan('contracts.read')
  const [collapsedTrades, setCollapsedTrades] = useState<Set<string>>(getCollapsedFromUrl)

  // Sync state from URL on popstate (back/forward navigation)
  useEffect(() => {
    const handler = () => {
      setFilter(getFilterFromUrl())
      setCollapsedTrades(getCollapsedFromUrl())
      // Sync worksite edit dialog from URL
      const wsUid = getWorksiteFromUrl()
      if (wsUid) {
        const ws = ((plan as any)?.worksites ?? []).find((w: Worksite) => w.uid === wsUid)
        if (ws) {
          setEditWorksite(ws)
          setShowEditWorksiteDialog(true)
        }
      } else {
        setShowEditWorksiteDialog(false)
      }
    }
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [plan])

  // On initial load, open edit dialog if worksite param is in URL
  useEffect(() => {
    if (!plan) return
    const wsUid = getWorksiteFromUrl()
    if (wsUid) {
      const ws = ((plan as any).worksites ?? []).find((w: Worksite) => w.uid === wsUid)
      if (ws) {
        setEditWorksite(ws)
        setShowEditWorksiteDialog(true)
      }
    }
  }, [plan?.uid]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = useCallback((f: FilterTab) => {
    setFilter(f)
    setCollapsedTrades((current) => {
      updateUrl(f, current)
      return current
    })
  }, [])

  const handleToggleTrade = useCallback((trade: string) => {
    setCollapsedTrades((current) => {
      const next = new Set(current)
      if (next.has(trade)) {
        next.delete(trade)
      } else {
        next.add(trade)
      }
      setFilter((f) => {
        updateUrl(f, next)
        return f
      })
      return next
    })
  }, [])

  // Workflow mutation hooks
  const submitMutation = useSubmitCapexPlan(org, planUid)
  const approveMutation = useApproveCapexPlan(org, planUid)
  const rejectMutation = useRejectCapexPlan(org, planUid)
  const generateMutation = useGenerateWorksites(org, planUid)

  const handleGenerate = useCallback(() => {
    generateMutation.mutate()
  }, [generateMutation])

  const handleSubmitConfirm = useCallback(async (note: string) => {
    try {
      await submitMutation.mutateAsync({ note: note || undefined })
    } catch {
      // handled by mutation state
    }
  }, [submitMutation])

  const handleApprove = useCallback(async () => {
    try {
      await approveMutation.mutateAsync()
    } catch {
      // handled by mutation state
    }
  }, [approveMutation])

  const handleRejectConfirm = useCallback(async (category: string, note: string) => {
    try {
      await rejectMutation.mutateAsync({
        category: category as 'budget' | 'priority' | 'timing' | 'missing' | 'scope' | 'other',
        note,
      })
    } catch {
      // handled by mutation state
    }
  }, [rejectMutation])

  // Resume as draft: plan is already back in draft after rejection per backend spec.
  // This is informational — no extra API call needed.
  const handleResumeDraft = useCallback(() => {}, [])

  // Worksite mutation hooks
  const queryClient = useQueryClient()
  const updateWorksiteMutation = useUpdateWorksite(org)
  const deleteWorksiteMutation = useDeleteWorksite(org)
  const deleteQuoteMutation = useDeleteQuote(org)

  // Optimistic update: apply worksite changes to the cached plan data immediately
  const applyOptimisticUpdate = useCallback(
    (uid: string, body: Record<string, unknown>) => {
      // Snapshot all matching cache entries for rollback
      const previousEntries: [unknown[], unknown][] = []

      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey as unknown[]
            return key.length >= 2 && key[0] === 'get' && typeof key[1] === 'string' && (key[1] as string).includes('capex-plans')
          },
        },
        (oldData: unknown) => {
          if (!oldData || typeof oldData !== 'object') return oldData
          const d = oldData as Record<string, unknown>
          const plan = (d.data ?? d) as Record<string, unknown>
          const worksites = plan.worksites as Worksite[] | undefined
          if (!worksites) return oldData

          const updated = worksites.map((ws) => {
            if (ws.uid !== uid) return ws
            return { ...ws, ...body }
          })

          if (d.data) {
            return { ...d, data: { ...plan, worksites: updated } }
          }
          return { ...plan, worksites: updated }
        }
      )

      return previousEntries
    },
    [queryClient]
  )

  const handleUpdateWorksite = useCallback(
    (uid: string, body: Record<string, unknown>, ifMatch: string) => {
      // Apply optimistic update immediately
      applyOptimisticUpdate(uid, body)

      updateWorksiteMutation.mutate({
        worksiteUid: uid,
        body: body as Parameters<typeof updateWorksiteMutation.mutate>[0]['body'],
        ifMatch,
      })
    },
    [updateWorksiteMutation, applyOptimisticUpdate]
  )

  const handleDeleteWorksite = useCallback(
    (uid: string) => {
      deleteWorksiteMutation.mutate(uid)
    },
    [deleteWorksiteMutation]
  )

  const handleQuote = useCallback((pkg: WorkPackage) => {
    setQuotePackage(pkg)
    setShowQuoteDialog(true)
  }, [])

  const handleDeleteQuote = useCallback((pkg: WorkPackage) => {
    setDeleteQuotePackage(pkg)
    setShowDeleteQuoteDialog(true)
  }, [])

  const handleAddPackage = useCallback((worksiteUid: string) => {
    setCreatePackageWorksiteUid(worksiteUid)
    setShowCreatePackageDialog(true)
  }, [])

  const handleEditWorksite = useCallback((ws: Worksite) => {
    setEditWorksite(ws)
    setShowEditWorksiteDialog(true)
    // Update URL to reflect edited worksite
    setFilter((f) => {
      setCollapsedTrades((c) => {
        updateUrl(f, c, ws.uid)
        return c
      })
      return f
    })
  }, [])

  const handleEditPackage = useCallback((pkg: WorkPackage) => {
    setEditPackage(pkg)
    setShowEditPackageDialog(true)
  }, [])

  const handleDeleteQuoteConfirm = useCallback(async () => {
    if (!deleteQuotePackage) return
    try {
      await deleteQuoteMutation.mutateAsync(deleteQuotePackage.uid!)
    } catch {
      // handled by mutation state
    }
    setShowDeleteQuoteDialog(false)
    setDeleteQuotePackage(null)
  }, [deleteQuotePackage, deleteQuoteMutation])

  // Breadcrumbs
  const assetName = (asset as Record<string, unknown>)?.name as string | undefined
  useBreadcrumbs(
    plan
      ? [
          { title: t('common:nav.home'), path: `/${org}` },
          { title: t('common:nav.assets'), path: `/${org}/assets` },
          ...(plan.asset_uid
            ? [{ title: assetName || plan.asset_uid, path: `/${org}/assets/${plan.asset_uid}?tab=capex` }]
            : []),
          { title: plan.name ?? '' },
        ]
      : []
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (error || !plan) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-destructive text-sm">{t('detail.load_error')}</p>
        <Link to="/$org/assets" params={{ org }} search={{} as any}>
          <Button variant="outline" size="sm">
            <AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />
            {t('detail.back')}
          </Button>
        </Link>
      </div>
    )
  }

  const workflowStatus = (plan.workflow_status ?? 'draft') as WorkflowStatus
  const isDraft = workflowStatus === 'draft'
  const isReadOnly = workflowStatus !== 'draft'
  const badgeVariant = WORKFLOW_BADGE_VARIANTS[workflowStatus] ?? 'secondary'

  // Extract worksites from the plan (included via ?include=worksites.work_packages)
  const worksites: Worksite[] = (plan as any).worksites ?? []

  // Compliance counts — currently always 0 since evaluations are not loaded from API yet.
  // These will be computed from worstWorksiteCompliance() once evaluations become available.
  const breachCount = 0
  const overdueCount = 0

  // Smoothing
  const handleSmoothClick = () => {
    const horizonStart = plan.horizon_start ?? new Date().getFullYear()
    const horizonEnd = plan.horizon_end ?? horizonStart + 10

    const { result, protectedCount } = smoothPlan(
      worksites,
      horizonStart,
      horizonEnd,
      () => null // no compliance evaluations loaded yet
    )

    // Find worksites that were moved
    const movedWorksites = result.filter((ws, i) => ws.year !== worksites[i]?.year)

    setSmoothPreview({ movedCount: movedWorksites.length, protectedCount, movedWorksites })
    setShowSmoothDialog(true)
  }

  const handleSmoothConfirm = () => {
    if (!smoothPreview) return
    setShowSmoothDialog(false)

    for (const ws of smoothPreview.movedWorksites) {
      const original = worksites.find(o => o.uid === ws.uid)
      if (!original || original.year === ws.year) continue

      updateWorksiteMutation.mutate({
        worksiteUid: ws.uid!,
        body: {
          year: ws.year,
          status: ws.status as any,
          deferred_from_year: ws.deferred_from_year,
        } as any,
        ifMatch: original.updated_at ?? '',
      })
    }

    setSmoothPreview(null)
  }

  return (
    // Every amount rendered below is in the plan's currency, resolved by the
    // API from the asset's jurisdiction. Provided once here rather than
    // threaded through six levels of components, where the next one added would
    // forget it and silently fall back to a euro sign.
    <CapexCurrencyProvider value={(plan.currency as string | undefined) ?? null}>
    <div className="space-y-6">
      {/* Plan header */}
      <PageHeader
        icon={SECTION_ICONS.capex}
        title={plan.name}
        description={`${t('horizon')}: ${plan.horizon_start}–${plan.horizon_end}`}
        titleAside={
          <>
            {plan.version !== undefined && (
              <Badge variant="outline">v{plan.version}</Badge>
            )}
            <Badge variant={badgeVariant}>
              {t(`workflow.${workflowStatus}`, workflowStatus)}
            </Badge>
          </>
        }
        actions={
          isDraft && (
            <Button
              onClick={() => setShowSubmitModal(true)}
              disabled={submitMutation.isPending}
              data-testid="submit-plan-btn"
            >
              <AppIcon path={mdiSend} size="sm" className="mr-1.5" />
              {t('detail.submit_plan')}
            </Button>
          )
        }
      />

      {/* Workflow banners */}
      <WorkflowBanners
        plan={plan}
        onApprove={handleApprove}
        onRejectClick={() => setShowRejectModal(true)}
        onResumeDraft={handleResumeDraft}
        isReadOnly={isReadOnly}
      />

      {/* Toolbar with KPI strip + filter tabs */}
      <PlanToolbar
        filter={filter}
        onFilterChange={handleFilterChange}
        worksites={worksites}
        isDraft={isDraft}
        onAddWorksite={() => setShowCreateWorksite(true)}
        onSmooth={handleSmoothClick}
        onGenerate={handleGenerate}
        isGenerating={generateMutation.isPending}
        breachCount={breachCount}
        overdueCount={overdueCount}
        unlockedRent={canReadContracts ? (plan as any).unlocked_rent : undefined}
        sortByUnlockedRent={sortByUnlockedRent}
        onToggleSort={() => setSortByUnlockedRent((v) => !v)}
      />

      {/* Plan grid */}
      <PlanGrid
        plan={plan}
        worksites={worksites}
        filter={filter}
        sortByUnlockedRent={sortByUnlockedRent}
        collapsedTrades={collapsedTrades}
        org={org}
        onToggleTrade={handleToggleTrade}
        isReadOnly={isReadOnly}
        onUpdateWorksite={handleUpdateWorksite}
        onDeleteWorksite={handleDeleteWorksite}
        onQuote={handleQuote}
        onDeleteQuote={handleDeleteQuote}
        onAddPackage={handleAddPackage}
        onEditWorksite={handleEditWorksite}
        onEditPackage={handleEditPackage}
      />

      {/* Modals */}
      <SubmitModal
        open={showSubmitModal}
        onOpenChange={setShowSubmitModal}
        plan={plan}
        worksites={worksites}
        onConfirm={handleSubmitConfirm}
      />

      <RejectModal
        open={showRejectModal}
        onOpenChange={setShowRejectModal}
        onConfirm={handleRejectConfirm}
      />

      <CreateWorksiteDialog
        open={showCreateWorksite}
        onOpenChange={setShowCreateWorksite}
        planUid={planUid}
        horizonStart={plan.horizon_start ?? new Date().getFullYear()}
        horizonEnd={plan.horizon_end ?? new Date().getFullYear() + 10}
        org={org}
        buildingAssetUid={plan.asset_uid}
      />

      {/* Create work package dialog */}
      <CreateWorkPackageDialog
        open={showCreatePackageDialog}
        onOpenChange={setShowCreatePackageDialog}
        worksiteUid={createPackageWorksiteUid ?? ''}
        org={org}
        buildingAssetUid={plan.asset_uid}
      />

      {/* Edit worksite dialog */}
      <EditWorksiteDialog
        open={showEditWorksiteDialog}
        onOpenChange={(open) => {
          setShowEditWorksiteDialog(open)
          if (!open) {
            // Clear worksite param from URL when dialog closes
            setFilter((f) => {
              setCollapsedTrades((c) => {
                updateUrl(f, c, null)
                return c
              })
              return f
            })
          }
        }}
        worksite={editWorksite}
        org={org}
        horizonStart={plan?.horizon_start ?? new Date().getFullYear()}
        horizonEnd={plan?.horizon_end ?? (plan?.horizon_start ?? new Date().getFullYear()) + 10}
        buildingAssetUid={plan?.asset_uid ?? undefined}
      />

      {/* Edit work package dialog */}
      <EditWorkPackageDialog
        open={showEditPackageDialog}
        onOpenChange={setShowEditPackageDialog}
        pkg={editPackage}
        org={org}
        buildingAssetUid={plan.asset_uid}
      />

      {/* Quote dialog */}
      <QuoteDialog
        open={showQuoteDialog}
        onOpenChange={setShowQuoteDialog}
        pkg={quotePackage}
        org={org}
      />

      {/* Delete quote confirmation */}
      <AlertDialog open={showDeleteQuoteDialog} onOpenChange={setShowDeleteQuoteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('quote.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('quote.delete_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('create_plan_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuoteConfirm}
              data-testid="quote-delete-confirm"
            >
              {t('quote.delete_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Smooth confirmation dialog */}
      <AlertDialog open={showSmoothDialog} onOpenChange={setShowSmoothDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('smooth.confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {smoothPreview && t('smooth.confirm_body', {
                moved: smoothPreview.movedCount,
                protected: smoothPreview.protectedCount,
              })}
              {smoothPreview?.movedCount === 0 && (
                <span className="block mt-2 text-muted-foreground">
                  {t('smooth.no_changes')}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('create_plan_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSmoothConfirm}
              disabled={!smoothPreview || smoothPreview.movedCount === 0}
              data-testid="smooth-confirm"
            >
              {t('smooth.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </CapexCurrencyProvider>
  )
}
